import React, { useEffect, useState } from "react";
import { ric } from "@/lib/ric";
import { useLang } from "@/context/LanguageContext";
import { Loader2, Plus, Truck, PackageCheck, Undo2 } from "lucide-react";

// MG's Yarn PO console — raise, watch, receive despatch (confirm/discrepancy),
// raise return. This is the MG-visible half of Phase 4.
export default function MGYarnPO() {
  const { lang } = useLang();
  const t = (en, hi) => (lang === "en" ? en : hi);
  const [pos, setPos] = useState([]);
  const [despatches, setDespatches] = useState([]);
  const [bins, setBins] = useState([]);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const [newPO, setNewPO] = useState({
    required_by: "",
    lines: [{ yarn_type: "Wool", yarn_count: "", yarn_ply: "", colour: "", kg: "", agreed_rate: "" }],
  });
  const [receiptDraft, setReceiptDraft] = useState({});   // po_line_id -> {kg_ok, discrepancy_kind, discrepancy_note}
  const [selectedDN, setSelectedDN] = useState({});         // po_id -> despatch_note_id
  const [returnDraft, setReturnDraft] = useState({});       // po_id -> {yarn_item_id, dye_lot, kg, to_bin_id, note}
  const [items, setItems] = useState([]);

  const load = async () => {
    setPos(await ric.listPOs());
    setDespatches(await ric.despatches());
    setBins(await ric.bins());
    setItems(await ric.yarnItems());
  };
  useEffect(() => { load(); }, []);

  const addLine = () => setNewPO({ ...newPO, lines: [...newPO.lines, { yarn_type: "Wool", yarn_count: "", yarn_ply: "", colour: "", kg: "", agreed_rate: "" }] });

  const submitNew = async (e) => {
    e.preventDefault(); setMsg("");
    if (newPO.lines.some((ln) => !ln.kg || !ln.yarn_type)) return setMsg(t("Fill kg + yarn type on every line", "हर लाइन पर कि. व यार्न ज़रूरी"));
    setBusy(true);
    try {
      await ric.raisePO({ ...newPO, lines: newPO.lines.map((ln) => ({ ...ln, kg: parseFloat(ln.kg), agreed_rate: ln.agreed_rate ? parseFloat(ln.agreed_rate) : null })) });
      setMsg(t("PO raised", "PO दर्ज"));
      setCreating(false);
      setNewPO({ required_by: "", lines: [{ yarn_type: "Wool", yarn_count: "", yarn_ply: "", colour: "", kg: "", agreed_rate: "" }] });
      await load();
    } catch (e) { setMsg(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const confirmReceipt = async (p) => {
    const dnId = selectedDN[p.id];
    if (!dnId) { setMsg(t("Select despatch note", "प्रेषण नोट चुनें")); return; }
    const lines = Object.entries(receiptDraft)
      .map(([lid, v]) => {
        const kg_ok = parseFloat(v.kg_ok || 0);
        const line = { po_line_id: lid, kg_ok };
        if (v.discrepancy_kind) line.discrepancy = { kind: v.discrepancy_kind, note: v.discrepancy_note || "" };
        return line;
      });
    setBusy(true);
    try {
      await ric.receipt(p.id, { despatch_note_id: dnId, lines });
      setMsg(t("Receipt confirmed — MG yarn purchases auto-created", "प्राप्ति दर्ज — MG यार्न खरीद स्वतः बनी"));
      setReceiptDraft({});
      await load();
    } catch (e) { setMsg(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const doReturn = async (p) => {
    const v = returnDraft[p.id];
    if (!v || !v.yarn_item_id || !v.dye_lot || !v.kg || !v.to_bin_id) {
      setMsg(t("Fill all return fields", "वापसी के सभी क्षेत्र भरें")); return;
    }
    setBusy(true);
    try {
      await ric.raiseReturn(p.id, { ...v, kg: parseFloat(v.kg), despatch_note_id: selectedDN[p.id] });
      setMsg(t("Return raised", "वापसी दर्ज"));
      setReturnDraft({ ...returnDraft, [p.id]: undefined });
      await load();
    } catch (e) { setMsg(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  return (
    <div data-testid="mg-yarn-po">
      <div className="flex justify-between items-start flex-wrap gap-2 mb-3">
        <h1 className="text-2xl font-bold" style={{ color: "var(--brand-navy)", fontFamily: "'Fraunces', serif" }}>
          {t("Yarn POs on RIC", "RIC पर यार्न PO")}
        </h1>
        <button data-testid="mg-yarn-new-btn" onClick={() => setCreating(!creating)}
          className="h-10 px-3 rounded-md text-sm font-semibold text-white flex items-center gap-1"
          style={{ background: "var(--brand-navy)" }}>
          <Plus size={14} /> {creating ? t("Cancel", "रद्द") : t("Raise new PO", "नया PO")}
        </button>
      </div>

      {msg && <div className="mb-3 text-xs bg-amber-50 border border-amber-200 rounded p-2" data-testid="mg-yarn-msg">{msg}</div>}

      {creating && (
        <form onSubmit={submitNew} className="hag-surface p-4 mb-4" data-testid="mg-yarn-po-form">
          <div className="mb-3">
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#4A627A]">Required by</label>
            <input data-testid="mg-yarn-req-by" type="date" value={newPO.required_by}
              onChange={(e) => setNewPO({ ...newPO, required_by: e.target.value })}
              className="w-full h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" />
          </div>
          {newPO.lines.map((ln, i) => (
            <div key={i} className="grid grid-cols-6 gap-1 mb-2">
              <input placeholder="type" value={ln.yarn_type} onChange={(e) => { const ls = [...newPO.lines]; ls[i].yarn_type = e.target.value; setNewPO({ ...newPO, lines: ls }); }}
                className="h-9 px-1 border border-[#1B3A5C]/15 rounded text-xs" data-testid={`mg-yarn-line-type-${i}`} />
              <input placeholder="count" value={ln.yarn_count} onChange={(e) => { const ls = [...newPO.lines]; ls[i].yarn_count = e.target.value; setNewPO({ ...newPO, lines: ls }); }}
                className="h-9 px-1 border border-[#1B3A5C]/15 rounded text-xs" data-testid={`mg-yarn-line-count-${i}`} />
              <input placeholder="ply" value={ln.yarn_ply} onChange={(e) => { const ls = [...newPO.lines]; ls[i].yarn_ply = e.target.value; setNewPO({ ...newPO, lines: ls }); }}
                className="h-9 px-1 border border-[#1B3A5C]/15 rounded text-xs" />
              <input placeholder="colour" value={ln.colour} onChange={(e) => { const ls = [...newPO.lines]; ls[i].colour = e.target.value; setNewPO({ ...newPO, lines: ls }); }}
                className="h-9 px-1 border border-[#1B3A5C]/15 rounded text-xs" />
              <input placeholder="kg" type="number" step="0.1" value={ln.kg} onChange={(e) => { const ls = [...newPO.lines]; ls[i].kg = e.target.value; setNewPO({ ...newPO, lines: ls }); }}
                className="h-9 px-1 border border-[#1B3A5C]/15 rounded text-xs text-right" data-testid={`mg-yarn-line-kg-${i}`} />
              <input placeholder="rate/kg" type="number" step="0.1" value={ln.agreed_rate} onChange={(e) => { const ls = [...newPO.lines]; ls[i].agreed_rate = e.target.value; setNewPO({ ...newPO, lines: ls }); }}
                className="h-9 px-1 border border-[#1B3A5C]/15 rounded text-xs text-right" />
            </div>
          ))}
          <div className="flex gap-2 mt-2">
            <button type="button" onClick={addLine} className="h-9 px-3 text-xs rounded-md border border-[#1B3A5C]/20">+ line</button>
            <button type="submit" data-testid="mg-yarn-submit" disabled={busy}
              className="h-9 px-4 rounded-md text-xs font-semibold text-white flex items-center gap-1"
              style={{ background: "var(--brand-navy)" }}>
              {busy && <Loader2 size={12} className="animate-spin" />} {t("Send to RIC", "RIC को भेजें")}
            </button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {pos.map((p) => {
          const myDN = despatches.filter((d) => d.yarn_po_id === p.id);
          const isOpen = openId === p.id;
          return (
            <div key={p.id} className="hag-surface p-3" data-testid={`mg-yarn-po-${p.id}`}>
              <div className="flex justify-between flex-wrap gap-2">
                <div>
                  <div className="text-sm font-semibold" style={{ color: "var(--brand-navy)" }}>{p.po_number}</div>
                  <div className="text-xs text-[#7C8E9E]">
                    {p.lines.length} lines · <b>{p.status}</b> · Despatches: {myDN.length}
                  </div>
                </div>
                <button onClick={() => setOpenId(isOpen ? "" : p.id)}
                  className="h-8 px-3 text-xs rounded-md border border-[#1B3A5C]/20"
                  data-testid={`mg-yarn-po-open-${p.id}`}>
                  {isOpen ? "Close" : "Open"}
                </button>
              </div>

              {isOpen && (
                <div className="mt-3 border-t border-[#1B3A5C]/10 pt-3">
                  <table className="w-full text-xs mb-3">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-widest text-[#7C8E9E]">
                        <th className="text-left">Line</th><th className="text-right">Req</th>
                        <th className="text-right">Acc</th><th className="text-right">Desp</th>
                        <th className="text-right">Recv</th><th className="text-right">Rate/kg</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.lines.map((ln) => (
                        <tr key={ln.id} className="border-t border-[#1B3A5C]/5">
                          <td className="py-1">{ln.yarn_type} · {ln.yarn_count} · {ln.colour}</td>
                          <td className="text-right">{ln.kg_requested}</td>
                          <td className="text-right">{ln.kg_accepted ?? "—"}</td>
                          <td className="text-right">{ln.kg_despatched || 0}</td>
                          <td className="text-right">{ln.kg_received || 0}</td>
                          <td className="text-right">{ln.agreed_rate ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {myDN.length > 0 && (
                    <div className="border-t border-[#1B3A5C]/10 pt-3 mb-3">
                      <div className="text-xs font-semibold mb-1 flex items-center gap-1"><Truck size={12} /> {t("Confirm receipt", "प्राप्ति की पुष्टि")}</div>
                      <select data-testid={`mg-yarn-dn-${p.id}`} value={selectedDN[p.id] || ""}
                        onChange={(e) => setSelectedDN({ ...selectedDN, [p.id]: e.target.value })}
                        className="w-full h-9 px-2 border border-[#1B3A5C]/15 rounded text-xs mb-2 bg-white">
                        <option value="">— {t("select despatch note", "प्रेषण नोट चुनें")} —</option>
                        {myDN.map((d) => <option key={d.id} value={d.id}>{d.id.slice(0, 8)} · {new Date(d.created_at).toLocaleDateString()} · {d.lines.length} lines</option>)}
                      </select>
                      {selectedDN[p.id] && (
                        <div className="space-y-2 mb-2">
                          {p.lines.map((ln) => {
                            const dnLine = myDN.find((d) => d.id === selectedDN[p.id])?.lines.find((l) => l.po_line_id === ln.id);
                            if (!dnLine) return null;
                            const v = receiptDraft[ln.id] || {};
                            return (
                              <div key={ln.id} className="grid grid-cols-4 gap-1 text-xs items-center" data-testid={`mg-yarn-recv-${ln.id}`}>
                                <div>{ln.yarn_type} · {ln.colour}</div>
                                <div>{t("Sent", "भेजा")}: {dnLine.kg} kg · {dnLine.dye_lot}</div>
                                <input placeholder="kg OK" type="number" step="0.1" value={v.kg_ok || ""}
                                  onChange={(e) => setReceiptDraft({ ...receiptDraft, [ln.id]: { ...v, kg_ok: e.target.value } })}
                                  className="h-8 px-1 border border-[#1B3A5C]/15 rounded text-xs text-right" data-testid={`mg-yarn-recv-kg-${ln.id}`} />
                                <select value={v.discrepancy_kind || ""}
                                  onChange={(e) => setReceiptDraft({ ...receiptDraft, [ln.id]: { ...v, discrepancy_kind: e.target.value || undefined } })}
                                  className="h-8 px-1 border border-[#1B3A5C]/15 rounded text-xs bg-white">
                                  <option value="">— disc? —</option>
                                  <option>short</option><option>damaged</option><option>wrong_item</option>
                                </select>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <button data-testid={`mg-yarn-recv-btn-${p.id}`} onClick={() => confirmReceipt(p)} disabled={busy || !selectedDN[p.id]}
                        className="h-9 px-3 rounded-md text-xs font-semibold text-white flex items-center gap-1 disabled:opacity-50"
                        style={{ background: "var(--brand-navy)" }}>
                        <PackageCheck size={12} /> {t("Confirm", "पुष्टि करें")}
                      </button>
                    </div>
                  )}

                  {myDN.length > 0 && (
                    <div className="border-t border-[#1B3A5C]/10 pt-3">
                      <div className="text-xs font-semibold mb-1 flex items-center gap-1"><Undo2 size={12} /> {t("Raise return", "वापसी दर्ज")}</div>
                      <div className="grid grid-cols-5 gap-1">
                        <select value={returnDraft[p.id]?.yarn_item_id || ""}
                          onChange={(e) => setReturnDraft({ ...returnDraft, [p.id]: { ...(returnDraft[p.id] || {}), yarn_item_id: e.target.value } })}
                          className="h-8 px-1 border border-[#1B3A5C]/15 rounded text-xs bg-white" data-testid={`mg-return-item-${p.id}`}>
                          <option value="">item</option>
                          {items.map((i) => <option key={i.id} value={i.id}>{i.yarn_type} · {i.colour}</option>)}
                        </select>
                        <select value={returnDraft[p.id]?.to_bin_id || ""}
                          onChange={(e) => setReturnDraft({ ...returnDraft, [p.id]: { ...(returnDraft[p.id] || {}), to_bin_id: e.target.value } })}
                          className="h-8 px-1 border border-[#1B3A5C]/15 rounded text-xs bg-white">
                          <option value="">→ bin</option>
                          {bins.map((b) => <option key={b.id} value={b.id}>{b.bin_number}</option>)}
                        </select>
                        <input placeholder="dye lot" value={returnDraft[p.id]?.dye_lot || ""}
                          onChange={(e) => setReturnDraft({ ...returnDraft, [p.id]: { ...(returnDraft[p.id] || {}), dye_lot: e.target.value } })}
                          className="h-8 px-1 border border-[#1B3A5C]/15 rounded text-xs" />
                        <input placeholder="kg" type="number" step="0.1" value={returnDraft[p.id]?.kg || ""}
                          onChange={(e) => setReturnDraft({ ...returnDraft, [p.id]: { ...(returnDraft[p.id] || {}), kg: e.target.value } })}
                          className="h-8 px-1 border border-[#1B3A5C]/15 rounded text-xs text-right" data-testid={`mg-return-kg-${p.id}`} />
                        <button onClick={() => doReturn(p)} disabled={busy}
                          className="h-8 px-2 rounded-md text-xs font-semibold text-white flex items-center gap-1"
                          style={{ background: "var(--brand-navy)" }} data-testid={`mg-return-btn-${p.id}`}>
                          <Undo2 size={12} /> {t("Return", "वापसी")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {pos.length === 0 && <div className="text-sm text-[#7C8E9E]">{t("No POs raised yet.", "अभी कोई PO नहीं।")}</div>}
      </div>
    </div>
  );
}
